import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonical?: string;
  ogType?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogUrl?: string;
  twitterTitle?: string;
  twitterDescription?: string;
  structuredData?: object;
}

export function SEO({
  title,
  description,
  keywords,
  canonical,
  ogType = 'website',
  ogTitle,
  ogDescription,
  ogUrl,
  twitterTitle,
  twitterDescription,
  structuredData
}: SEOProps) {
  const location = useLocation();
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  const currentUrl = `${siteUrl}${location.pathname}`;

  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Update meta tags
    const updateMetaTag = (name: string, content: string, isProperty = false) => {
      const attribute = isProperty ? 'property' : 'name';
      let element = document.querySelector(`meta[${attribute}="${name}"]`);
      
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(attribute, name);
        document.head.appendChild(element);
      }
      
      element.setAttribute('content', content);
    };

    if (description) {
      updateMetaTag('description', description);
    }

    if (keywords) {
      updateMetaTag('keywords', keywords);
    }

    // Update canonical
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonical || currentUrl);

    // Update last-modified
    const today = new Date().toISOString().split('T')[0];
    updateMetaTag('last-modified', today);

    // Update Open Graph tags
    updateMetaTag('og:type', ogType, true);
    if (ogTitle) updateMetaTag('og:title', ogTitle, true);
    if (ogDescription) updateMetaTag('og:description', ogDescription, true);
    updateMetaTag('og:url', ogUrl || currentUrl, true);

    // Update Twitter tags
    if (twitterTitle) updateMetaTag('twitter:title', twitterTitle);
    if (twitterDescription) updateMetaTag('twitter:description', twitterDescription);

    // Add structured data
    if (structuredData) {
      let script = document.querySelector('script[type="application/ld+json"]');
      
      if (!script) {
        script = document.createElement('script');
        script.setAttribute('type', 'application/ld+json');
        document.head.appendChild(script);
      }
      
      script.textContent = JSON.stringify(structuredData);
    }
  }, [title, description, keywords, canonical, ogType, ogTitle, ogDescription, ogUrl, twitterTitle, twitterDescription, structuredData, currentUrl]);

  return null;
}

// Generate Organization Schema
export function generateOrganizationSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "SALL Engineering",
    "alternateName": "SALL Collection Tekstil Sanayi ve Ticaret A.Ş.",
    "url": siteUrl,
    "logo": `${siteUrl}/logo.png`,
    "description": "SALL Engineering delivers advanced renewable energy engineering and consulting services. Specializing in solar PV systems, wind power solutions, battery energy storage, and grid integration across global markets.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Istanbul",
      "addressRegion": "Istanbul",
      "addressCountry": "TR"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": "41.0082",
      "longitude": "28.9784"
    },
    "contactPoint": {
      "@type": "ContactPoint",
      "contactType": "Customer Service",
      "email": "info@sallengineering.com"
    },
    "sameAs": [
      "https://linkedin.com/company/sall-engineering",
      "https://twitter.com/sallengineering"
    ]
  };
}

// Generate WebSite Schema
export function generateWebSiteSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "SALL Engineering",
    "url": siteUrl,
    "description": "Global renewable energy engineering and consulting services",
    "publisher": {
      "@type": "Organization",
      "name": "SALL Engineering"
    }
  };
}

// Generate Service Schema
export function generateServiceSchema() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Renewable Energy Engineering",
    "provider": {
      "@type": "Organization",
      "name": "SALL Engineering",
      "url": siteUrl
    },
    "areaServed": {
      "@type": "Place",
      "name": "Global"
    },
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Renewable Energy Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Solar Energy Systems",
            "description": "Utility-scale and commercial solar PV engineering services including feasibility studies, system design, performance modeling, and optimization."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Wind Power Solutions",
            "description": "Advanced wind farm development, turbine selection, and performance analysis for onshore and offshore wind energy projects."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Battery Storage",
            "description": "Energy storage system integration, BESS design, and grid-scale battery solutions for enhanced power reliability and flexibility."
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Grid Integration",
            "description": "Power system studies, grid connection engineering, and compliance solutions for seamless renewable energy integration."
          }
        }
      ]
    }
  };
}

// Generate BreadcrumbList Schema
export function generateBreadcrumbSchema(items: Array<{ name: string; url: string }>) {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';
  
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": `${siteUrl}${item.url}`
    }))
  };
}